// ANT Modules
import { Form, FormInstance, Input, Select } from "antd";

// Components
import Label from "components/atoms/Label/Label";
import ErrorMessage from "components/molecules/ErrorMessage/ErrorMessage";
import DateTimePicker from "components/atoms/DateTimePicker/DateTimePicker";
import InputPrefix from "components/atoms/InputPrefix/InputPrefix";
import OutletOption from "components/atoms/OutletOption/OutletOption";

// Styles
import styles from "components/organisms/ReviewClaimDetails/reviewClaimDetails.module.css";
import theme from "styles/theme";

// Type Definitions
import type { DisabledFieldsType, ReviewClaimDetailsType, ReviewClaimFieldErrors, FilledDataValidationType } from "containers/ReviewRetrospectiveClaim/useReviewRetrospectiveClaim";
import type { OutletItem } from "services/api/types";

// Constants & Utils
import { REVIEW_RETRO_CLAIM_FORM as Constants } from "constants/global";
import getIcon from "utils/getIcon";

const { Option } = Select;

export type ReviewClaimDetailsProps = {
	reviewClaimDetails: ReviewClaimDetailsType;
	disableFields: DisabledFieldsType;
	outletCode: string;
	outletName: string;
	terminal: string;
	terminalsList: Array<{ id: string; value: string }>;
	filteredOutletsList: Array<OutletItem>;
	form?: FormInstance<any>;
	handleTerminalSelect: (v: string) => void;
	handleOutletSearch: (value: string) => void;
	handleOutletSelect: (value: string) => void;
	handleInputChange: (e: any) => void;
	validateFields: (arg: boolean, keyForReValidation?: string) => void;
	fieldErrors?: ReviewClaimFieldErrors;
	handleDateChange?: (date: any, dateString: string) => void;
	handleDateOk?: (value: moment.Moment) => void;
	outletsList?: Array<OutletItem>;
	filledDataValidation?: FilledDataValidationType;
	isOutletInactive?: boolean;
	claimStatus?: string;
};

// Applying styles based on fieldErrors
const fieldStyle = (isErrorPresent: boolean) => {
	return isErrorPresent ? styles?.err : styles?.filled;
};

const filledDataStyle = (isError?: boolean) => {
	return isError ? styles?.err : "";
};

const disableBorderStyle = (isDisabled?: boolean) => {
	return isDisabled ? styles?.disabled : styles?.err;
};

const ReviewClaimDetails = ({
	reviewClaimDetails,
	disableFields,
	handleInputChange,
	handleDateChange,
	handleDateOk,
	validateFields,
	fieldErrors,
	outletCode,
	outletName,
	handleOutletSearch,
	handleOutletSelect,
	filteredOutletsList,
	terminalsList,
	terminal,
	form,
	handleTerminalSelect,
	filledDataValidation,
	isOutletInactive,
	claimStatus
}: ReviewClaimDetailsProps): JSX.Element => {
	const filledDataCheck = !filledDataValidation?.net_amount ? disableBorderStyle(disableFields?.net_amount) : filledDataStyle(filledDataValidation?.net_amount);

	return (
		<Form form={form} className={styles?.form} requiredMark={false} layout="vertical">
			<div className="review-claim-form">
				<div className="mt-5 md:mt-0 md:flex">
					<Form.Item name="status" label={<Label label={Constants.STATUS_LABEL} classes={{ label: styles?.basic_input_label }} />}>
						<p className={`${styles?.basic_text_purple}`}>{reviewClaimDetails?.status?.replace("_", " ")}</p>
					</Form.Item>
					<Form.Item name="source" label={<Label label={Constants.SOURCE_LABEL} classes={{ label: styles?.basic_input_label }} />}>
						<p className={`${styles?.basic_text}`}>{reviewClaimDetails?.source}</p>
					</Form.Item>
				</div>
				<Form.Item
					initialValue={reviewClaimDetails?.submission_datetime}
					name="submission_datetime"
					label={<Label label={Constants.SUBMISSION_DATETIME} classes={{ label: styles?.basic_input_label }} />}
				>
					<Input disabled data-testid={Constants.CARD_NUMBER} className={`${styles?.basic_input_small}`} />
				</Form.Item>
				<div className="md:flex">
					<Form.Item initialValue={reviewClaimDetails?.username} name="username" label={<Label label={Constants.USERNAME} classes={{ label: styles?.basic_input_label }} />}>
						<Input disabled data-testid={Constants.USERNAME} className={`${styles?.basic_input_small}`} />
					</Form.Item>
					<Form.Item initialValue={reviewClaimDetails?.agent_id} name="agent_id" label={<Label label={Constants.AGENT_ID} classes={{ label: styles?.basic_input_label }} />}>
						<Input disabled className={`${styles?.basic_input_small}`} />
					</Form.Item>
				</div>
				<div className="md:flex">
					<Form.Item
						extra={fieldErrors?.receipt_number && <ErrorMessage classes={styles?.err_message} message={Constants.ERROR_MESSAGE.RECEIPT_NUMBER} />}
						name="receipt_number"
						initialValue={reviewClaimDetails.receipt_number}
						label={<Label label={Constants.RECEIPT_NUMBER} classes={{ label: styles?.basic_input_label }} requiredMark />}
					>
						<Input
							data-testid={Constants.RECEIPT_NUMBER}
							placeholder={Constants.RECEIPT_NUMBER_PLACEHOLDER}
							className={`${styles?.basic_input_small} ${
								!disableFields?.receipt_number && reviewClaimDetails.receipt_number ? fieldStyle(!!fieldErrors?.receipt_number) : disableBorderStyle(disableFields?.receipt_number)
							}`}
							value={reviewClaimDetails.receipt_number}
							onChange={handleInputChange}
							onBlur={() => {
								validateFields(false, "receipt_number");
							}}
							disabled={disableFields?.receipt_number}
						/>
					</Form.Item>
					<Form.Item name="receipt_datetime" label={<Label label={Constants.RECEIPT_DATE_TIME} classes={{ label: styles?.basic_input_label }} requiredMark />}>
						<DateTimePicker
							classes={`${styles?.basic_input_small} ${
								!disableFields?.receipt_datetime && reviewClaimDetails?.receipt_datetime && !filledDataValidation?.receipt_datetime
									? styles?.filled
									: filledDataStyle(filledDataValidation?.receipt_datetime)
							}`}
							placeholder={Constants.RECEIPT_DATE_TIME_PLACEHOLDER}
							onDateChange={handleDateChange}
							onDateOk={handleDateOk}
							defaultDate={reviewClaimDetails?.receipt_datetime}
							disabled={disableFields?.receipt_datetime ? true : false}
						/>
					</Form.Item>
				</div>
				<div className="md:flex">
					<Form.Item
						initialValue={reviewClaimDetails?.cr_card_number}
						extra={fieldErrors?.cr_card_number && <ErrorMessage classes={styles?.err_message} message={Constants.ERROR_MESSAGE.CARD_NUMBER} />}
						name="cr_card_number"
						label={<Label label={Constants.CARD_NUMBER} classes={{ label: styles?.basic_input_label }} requiredMark />}
					>
						<Input
							data-testid={Constants.CARD_NUMBER}
							placeholder={Constants.CARD_NUMBER_PLACEHOLDER}
							className={`${styles?.basic_input_small} ${
								!disableFields?.cr_card_number && reviewClaimDetails.cr_card_number ? fieldStyle(!!fieldErrors?.cr_card_number) : disableBorderStyle(disableFields?.cr_card_number)
							}`}
							value={reviewClaimDetails.cr_card_number}
							onChange={handleInputChange}
							onBlur={() => {
								validateFields(false, "cr_card_number");
							}}
							disabled={disableFields?.cr_card_number}
							pattern="[0-9]"
							maxLength={19}
						/>
					</Form.Item>
					<Form.Item name="terminal" initialValue={`${terminal}`} label={<Label label={Constants.TERMINAL} classes={{ label: styles?.basic_input_label }} requiredMark />}>
						<Select
							data-testid={Constants.TERMINAL}
							disabled={disableFields?.outlet_code}
							showSearch
							className={`outlet-select ${styles?.basic_input_select} ${!disableFields?.outlet_code && outletCode ? styles?.filled : ""}`}
							placeholder={Constants.TERMINAL_PLACEHOLDER}
							optionFilterProp="children"
							onChange={handleTerminalSelect}
							value={terminal}
							listHeight={325}
							style={{ width: "100%", minWidth: "12.5rem" }}
							popupClassName="left-nav-search-dropdown"
							dropdownStyle={{ backgroundColor: theme.dropdownStyle.backgroundColor, boxShadow: "none" }}
							suffixIcon={<span>{getIcon({ icon: "" })}</span>}
							filterOption={(input, option) => (option?.value as unknown as string).toLowerCase().includes(input.toLowerCase())}
						>
							{terminalsList?.map((item: { id: string; value: string }) => {
								return (
									<Option value={`${item.value}`} key={item.id} className="flex-col px-2 py-2">
										{`${item.value}`}
									</Option>
								);
							})}
						</Select>
					</Form.Item>
				</div>
				<Form.Item
					name="outlet_code"
					initialValue={`${outletName} ${outletCode}`}
					label={<Label label={Constants.OUTLET} classes={{ label: styles?.basic_input_label }} requiredMark />}
					extra={
						isOutletInactive && (
							<div className={styles?.err_message}>
								<ErrorMessage
									classes=""
									message={
										claimStatus?.toUpperCase() === Constants.REJECTED_STATUS || claimStatus?.toUpperCase() === Constants.APPROVED_STATUS ? (
											"Selected outlet is inactive."
										) : (
											<>
												Selected outlet is inactive. Please select a terminal first,
												<br />
												then select an active outlet.
											</>
										)
									}
								/>
							</div>
						)
					}
				>
					<Select
						data-testid={Constants.OUTLET}
						disabled={!terminal || disableFields?.outlet_code}
						showSearch
						className={`crsc_outlet_select ${styles?.outlet_selection} ${!disableFields?.outlet_code && outletCode ? styles?.filled : ""}`}
						placeholder={Constants.OUTLET_PLACEHOLDER}
						optionFilterProp="children"
						onChange={handleOutletSelect}
						onSearch={handleOutletSearch}
						value={outletCode}
						listHeight={325}
						style={{ width: "100%", minWidth: "12.5rem" }}
						popupClassName="left-nav-search-dropdown"
						dropdownStyle={{ backgroundColor: theme.dropdownStyle.backgroundColor, boxShadow: "none" }}
						suffixIcon={<span>{getIcon({ icon: "" })}</span>}
						filterOption={(input, option) => (option?.value as unknown as string).toLowerCase().includes(input.toLowerCase())}
					>
						{filteredOutletsList?.map((item: OutletItem) => {
							return (
								<Option value={`${item.outlet_name}*${item.outlet_code}*${item.terminal}*${item?.location || ""}`} key={item.outlet_code} className="flex-col px-2 py-2">
									<OutletOption outlet_name={item.outlet_name} outlet_code={item.outlet_code} terminal={item?.terminal} location={item?.location} />
								</Option>
							);
						})}
					</Select>
				</Form.Item>
				<div className="md:flex">
					<Form.Item
						name="purchase_amount"
						extra={fieldErrors?.purchase_amount && <ErrorMessage classes={styles?.err_message} message={Constants.ERROR_MESSAGE.PURCHASE_AMOUNT} />}
						initialValue={reviewClaimDetails?.purchase_amount}
						label={<Label label={Constants.PURCHASE_AMOUNT} classes={{ label: styles?.basic_input_label }} requiredMark />}
					>
						<Input
							pattern="[0-9]"
							min={0}
							data-testid={Constants.PURCHASE_AMOUNT}
							placeholder={Constants.PURCHASE_AMOUNT_PLACEHOLDER}
							className={`${styles?.basic_input_small} ${
								!disableFields?.purchase_amount && reviewClaimDetails.purchase_amount
									? fieldStyle(!!fieldErrors?.purchase_amount)
									: disableBorderStyle(disableFields?.purchase_amount)
							}`}
							value={reviewClaimDetails.purchase_amount}
							onChange={handleInputChange}
							onBlur={() => {
								validateFields(false, "purchase_amount");
							}}
							disabled={disableFields?.purchase_amount}
							prefix={<InputPrefix prefixText={Constants.AMOUNT_INPUT_PREFIX} />}
						/>
					</Form.Item>
					<Form.Item
						name="net_amount"
						extra={fieldErrors?.net_amount && <ErrorMessage classes={styles?.err_message} message={Constants.ERROR_MESSAGE.NET_AMOUNT} />}
						initialValue={reviewClaimDetails?.net_amount}
						label={<Label label={Constants.NET_AMOUNT} classes={{ label: styles?.basic_input_label }} requiredMark />}
					>
						<Input
							pattern="[0-9]"
							min={0}
							data-testid={Constants.NET_AMOUNT}
							placeholder={Constants.NET_AMOUNT_PLACEHOLDER}
							className={`${styles?.basic_input_small} ${
								!disableFields?.net_amount && reviewClaimDetails.net_amount ? fieldStyle(!!fieldErrors?.net_amount || !!filledDataValidation?.net_amount) : filledDataCheck
							}`}
							value={reviewClaimDetails.net_amount}
							onChange={handleInputChange}
							onBlur={() => {
								validateFields(false, "net_amount");
							}}
							disabled={disableFields?.net_amount}
							prefix={<InputPrefix prefixText={Constants.AMOUNT_INPUT_PREFIX} />}
						/>
					</Form.Item>
				</div>
			</div>
		</Form>
	);
};

export default ReviewClaimDetails;
